// The app shell (chrome): brand bar, a framed "stage" region for the studio,
// and a status bar. Pre-start it shows the landing; after the user starts
// (audio unlock) the app swaps the studio in.

// Logo: stacked loop layers crossed by a playhead — what the app actually is.
const LOGO = `
<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
  <rect x="5" y="8.5" width="13" height="2.6" rx="1.3" fill="currentColor"/>
  <rect x="5" y="14.7" width="20" height="2.6" rx="1.3" fill="currentColor" opacity="0.55"/>
  <rect x="5" y="20.9" width="9" height="2.6" rx="1.3" fill="currentColor"/>
  <rect x="21" y="5" width="2.4" height="22" rx="1.2" fill="currentColor" opacity="0.9"/>
  <circle cx="22.2" cy="5" r="2.4" fill="currentColor"/>
</svg>`;

export interface RecentSession {
  id: string;
  title: string;
  updatedAt: number;
}

export interface ShellHandlers {
  onStart(): void;
  onResume(id: string): void;
  onRenameTitle(name: string): void;
  primaryLabel: string;
  recent: RecentSession[];
}

export interface ShellApi {
  enterStudio(content: HTMLElement): void;
  setLive(on: boolean): void;
  /** Place toolbar content (e.g. the Settings button) in the top bar. */
  mountTools(el: HTMLElement): void;
  /** Show + set the session title in the center of the top bar (renamable). */
  setTitle(name: string): void;
}

export function mountShell(root: HTMLElement, handlers: ShellHandlers): ShellApi {
  root.innerHTML = "";

  const app = document.createElement("div");
  app.className = "app";

  const topbar = document.createElement("header");
  topbar.className = "topbar";
  const brand = document.createElement("div");
  brand.className = "brand";
  const brandMark = document.createElement("span");
  brandMark.className = "brand__mark";
  brandMark.innerHTML = LOGO;
  const brandName = document.createElement("span");
  brandName.className = "brand__name";
  brandName.textContent = "loopfield";
  brand.append(brandMark, brandName);

  // Center: the session title (renamable). Hidden until we enter the studio.
  let currentTitle = "";
  const titleBtn = document.createElement("button");
  titleBtn.type = "button";
  titleBtn.className = "topbar__title";
  titleBtn.title = "Rename session";
  titleBtn.hidden = true;
  const startTitleRename = (): void => {
    const input = document.createElement("input");
    input.type = "text";
    input.className = "topbar__title-input";
    input.value = currentTitle;
    input.maxLength = 60;
    input.setAttribute("aria-label", "Session name");
    titleBtn.replaceWith(input);
    input.focus();
    input.select();
    let done = false;
    const commit = (save: boolean): void => {
      if (done) return;
      done = true;
      const name = input.value.trim() || currentTitle;
      input.replaceWith(titleBtn);
      if (save && name !== currentTitle) {
        currentTitle = name;
        titleBtn.textContent = name;
        handlers.onRenameTitle(name);
      } else {
        titleBtn.textContent = currentTitle;
      }
    };
    input.addEventListener("blur", () => commit(true));
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") input.blur();
      else if (e.key === "Escape") {
        input.value = currentTitle;
        input.blur();
      }
    });
  };
  titleBtn.addEventListener("click", startTitleRename);

  const tools = document.createElement("div");
  tools.className = "topbar__tools"; // the app drops a Settings button here
  topbar.append(brand, titleBtn, tools);

  const stage = document.createElement("main");
  stage.className = "stage";
  const content = document.createElement("div");
  content.className = "stage__content";
  content.append(buildLanding(handlers));
  stage.append(content);

  const statusbar = document.createElement("footer");
  statusbar.className = "statusbar";
  const statusEl = document.createElement("span");
  statusEl.className = "status";
  const dot = document.createElement("span");
  dot.className = "status__dot";
  const statusText = document.createElement("span");
  statusText.textContent = "Ready";
  statusEl.append(dot, statusText);
  const note = document.createElement("span");
  note.className = "statusbar__note";
  note.textContent = "Audio is processed on your device. Nothing is uploaded.";
  statusbar.append(statusEl, note);

  app.append(topbar, stage, statusbar);

  // Small screens (phones): show a "open on a larger screen" notice instead of
  // a cramped, unusable studio. Tablets and up get the real app. CSS decides
  // which is visible (width-based), so both stay in the DOM.
  const gate = document.createElement("div");
  gate.className = "device-gate";
  gate.innerHTML = `
    <div class="device-gate__card">
      <div class="device-gate__mark">${LOGO}</div>
      <h1 class="device-gate__title">loopfield</h1>
      <p class="device-gate__body">Loopfield needs a bigger canvas. Please open it on a laptop, desktop, or tablet to build your loops.</p>
    </div>`;

  root.append(app, gate);

  return {
    enterStudio(studio) {
      content.classList.add("stage__content--studio");
      content.replaceChildren(studio);
    },
    setLive(on) {
      statusEl.classList.toggle("is-live", on);
      statusText.textContent = on ? "Live" : "Ready";
    },
    mountTools(el) {
      tools.replaceChildren(el);
    },
    setTitle(name) {
      currentTitle = name;
      titleBtn.textContent = name;
      titleBtn.hidden = false;
    },
  };
}

function buildLanding(handlers: ShellHandlers): HTMLElement {
  const landing = document.createElement("div");
  landing.className = "landing";

  const card = document.createElement("div");
  card.className = "landing__card";

  const mark = document.createElement("div");
  mark.className = "landing__mark";
  mark.innerHTML = LOGO;

  const title = document.createElement("h1");
  title.className = "landing__title";
  title.textContent = "loopfield";

  const tag = document.createElement("p");
  tag.className = "landing__tag";
  tag.textContent = "Loop it. Stack it. Done.";

  const sub = document.createElement("p");
  sub.className = "landing__sub";
  sub.textContent = "Build a song from short loops with your keyboard, pads, or voice, all in your browser.";

  const start = document.createElement("button");
  start.className = "btn-start";
  start.type = "button";
  start.textContent = handlers.primaryLabel;
  start.addEventListener("click", handlers.onStart);

  card.append(mark, title, tag, sub, start);

  if (handlers.recent.length > 0) {
    const recent = document.createElement("div");
    recent.className = "recent";
    const rtitle = document.createElement("div");
    rtitle.className = "recent__title";
    rtitle.textContent = "Recent sessions";
    const rlist = document.createElement("div");
    rlist.className = "recent__list";
    for (const s of handlers.recent.slice(0, 4)) {
      const item = document.createElement("button");
      item.type = "button";
      item.className = "recent__item";
      const name = document.createElement("span");
      name.className = "recent__name";
      name.textContent = s.title || "Untitled";
      const when = document.createElement("span");
      when.className = "recent__when";
      when.textContent = relativeTime(s.updatedAt);
      item.append(name, when);
      item.addEventListener("click", () => handlers.onResume(s.id));
      rlist.append(item);
    }
    recent.append(rtitle, rlist);
    card.append(recent);
  }

  const privacy = document.createElement("p");
  privacy.className = "landing__privacy";
  privacy.textContent = "Everything stays on your device. Nothing is uploaded.";
  card.append(privacy);

  landing.append(card);
  return landing;
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const min = Math.round(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}
